import JsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { pdfHeader } from "@/utilities/pdfUtils";

/**
 * Status label mappings
 */
const getStatusLabels = (t) => ({
  cheque: {
    VALIDATED: t("tresorerie.valide"),
    PENDING: t("tresorerie.enAttente"),
    REJECTED: t("tresorerie.rejete"),
  },
  bordereau: {
    PENDING: t("tresorerie.enAttente"),
    VALIDATED: t("tresorerie.valide"),
    REJECTED: t("tresorerie.rejete"),
  },
  bordereauDepot: {
    NON_DEPOSE: t("tresorerie.nonDepose"),
    DEPOSE: t("tresorerie.depose"),
    TRAITE: t("tresorerie.traite"),
  },
});

/**
 * Get label for cheque bank status
 * @param {string} status - Bank status value
 * @param {Function} t - Translation function
 * @returns {string} - Human readable label
 */
const getChequeStatusLabel = (status, t) => {
  const statusLabels = getStatusLabels(t);
  return statusLabels.cheque[status] || status;
};

/**
 * Get label for bordereau status
 * @param {string} status - Bordereau status value
 * @param {Function} t - Translation function
 * @returns {string} - Human readable label
 */
const getBordereauStatusLabel = (status, t) => {
  const statusLabels = getStatusLabels(t);
  return statusLabels.bordereau[status] || status;
};

/**
 * Get label for bordereau depot status
 * @param {string} status - Bordereau depot status value
 * @param {Function} t - Translation function
 * @returns {string} - Human readable label
 */
const getBordereauDepotStatusLabel = (status, t) => {
  const statusLabels = getStatusLabels(t);
  return statusLabels.bordereauDepot[status] || status;
};

/**
 * Common PDF table styles
 */
const TABLE_STYLES = {
  theme: "grid",
  styles: {
    font: "Amiri",
    valign: "middle",
    halign: "center",
    fontSize: 9,
    cellPadding: 4,
    overflow: "linebreak",
    cellWidth: "wrap",
  },
  headStyles: {
    fontStyle: "bold",
    fontSize: 10,
    fillColor: [52, 73, 94],
    textColor: [255, 255, 255],
  },
  alternateRowStyles: {
    fillColor: [248, 249, 250],
  },
  margin: { top: 70, left: 14, right: 14 },
  tableWidth: "auto",
  showHead: "everyPage",
};

/**
 * Add page numbers to the PDF document
 * @param {Object} doc - jsPDF document instance
 */
const addPageNumbers = (doc, t) => {
  const pages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  doc.setFontSize(9);
  doc.setFont(undefined, "normal");

  for (let j = 1; j <= pages; j++) {
    const horizontalPos = pageWidth / 2;
    const verticalPos = pageHeight - 15;
    doc.setPage(j);

    doc.text(
      `${t("pdf.pageNumber", { current: j, total: pages })}`,
      horizontalPos,
      verticalPos,
      {
        align: "center",
      }
    );

    doc.setDrawColor(200, 200, 200);
    doc.line(
      horizontalPos - 20,
      verticalPos - 2,
      horizontalPos + 20,
      verticalPos - 2
    );
  }
};

/**
 * Check if summary section needs a new page
 * @param {Object} doc - jsPDF document instance
 * @param {number} finalY - Current Y position
 * @param {number} requiredSpace - Space needed for summary
 * @returns {boolean} - True if new page needed
 */
const needsNewPage = (doc, finalY, requiredSpace) => {
  const pageHeight = doc.internal.pageSize.height;
  return finalY + requiredSpace > pageHeight - 30;
};

/**
 * Add summary section to PDF
 * @param {Object} doc - jsPDF document instance
 * @param {Object} summaryData - Summary data object
 * @param {number} startY - Starting Y position
 * @param {boolean} isNewPage - Whether this is on a new page
 */
const addSummarySection = (
  doc,
  summaryData,
  startY,
  isNewPage = false,
  locale = "fr-FR"
) => {
  const { title, items, breakdowns } = summaryData;
  const isArabic = locale === "ar-AR";
  const pageWidth = doc.internal.pageSize.getWidth();
  const textX = isArabic ? pageWidth - 14 : 14;
  const itemX = isArabic ? pageWidth - 20 : 20;

  doc.setFontSize(11);
  doc.setFont("Amiri", "Bold");
  doc.text(title, textX, startY, { align: isArabic ? "right" : "left" });

  doc.setFontSize(10);
  doc.setFont("Amiri", "normal");

  // Add summary items
  items.forEach((item, index) => {
    doc.text(item, textX, startY + 8 + index * 5, {
      align: isArabic ? "right" : "left",
    });
  });

  // Add breakdowns
  let yPosition = startY + 25;
  breakdowns.forEach((breakdown) => {
    doc.setFont("Amiri", "Bold");
    doc.text(breakdown.title, textX, yPosition, {
      align: isArabic ? "right" : "left",
    });

    yPosition += 6;
    doc.setFont("Amiri", "normal");

    breakdown.items.forEach((item) => {
      doc.text(item, itemX, yPosition, { align: isArabic ? "right" : "left" });
      yPosition += 5;
    });

    yPosition += 5;
  });
};

/**
 * Build filter text for PDF header
 * @param {Object} filters - Applied filters
 * @param {Array} dateRange - Date range filter
 * @returns {string} - Formatted filter text
 */
const buildFilterText = (filters, dateRange) => {
  const filterParts = [];

  if (dateRange && dateRange[0]) {
    const startDate = dateRange[0]._d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const endDate = dateRange[1]._d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    filterParts.push(`Période: ${startDate} à ${endDate}`);
  }

  if (filters.type) {
    filterParts.push(`Type: ${filters.type}`);
  }

  if (filters.bankStatus) {
    const statusText = getChequeStatusLabel(filters.bankStatus);
    filterParts.push(`Statut Banque: ${statusText}`);
  }

  if (filters.status) {
    const statusText = getBordereauStatusLabel(filters.status);
    filterParts.push(`Statut: ${statusText}`);
  }

  if (filters.statutDepot) {
    const depotText = getBordereauDepotStatusLabel(filters.statutDepot);
    filterParts.push(`Statut Dépôt: ${depotText}`);
  }

  if (filters.search) {
    filterParts.push(`Recherche: "${filters.search}"`);
  }

  return filterParts.join(" | ");
};

/**
 * Generate PDF for cheques and traites
 * @param {Object} options - PDF generation options
 * @param {Array} options.data - Filtered table data
 * @param {Array} options.dateRange - Date range filter [start, end]
 * @param {Object} options.settings - Application settings
 * @param {Object} options.filters - Applied filters (type, status, search)
 * @returns {Object} - Result object with success status and message
 */
export const generateChequeTraitePdf = ({
  data,
  dateRange,
  settings,
  filters = {},
  t,
  locale = "fr-FR",
}) => {
  try {
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);
    const filterText = buildFilterText(filters, dateRange);
    const doc = pdfHeader(settings, {
      title: t("tresorerie.chequesTraites"),
      subTitle: filterText,
      locale: locale,
    });

    // Prepare data and calculate totals
    const chequeData = [];
    let total = 0;
    const typeCounts = { CHEQUE: 0, TRAITE: 0, VIREMENT: 0 };

    data.forEach((cheque) => {
      total += Number(cheque.montant) || 0;
      typeCounts[cheque.type] = (typeCounts[cheque.type] || 0) + 1;

      const bankName = cheque.bank ? cheque.bank.split("-")[0] : "--";
      let rowData = [
        moment(cheque.createdAt).format("DD/MM/YYYY"),
        cheque.num || "--",
        cheque.type || "--",
        bankName,
        cheque.titulaire || "--",
        getChequeStatusLabel(cheque.bankStatus, t) || "--",
        cheque.relatedInvoice ? cheque.relatedInvoice.serial : "--",
        cheque.dateEchance
          ? moment(cheque.dateEchance).format("DD/MM/YYYY")
          : "--",
        Number(cheque.montant).toFixed(2) || "0.00",
      ];

      // Reverse for Arabic if needed
      if (isArabic) {
        rowData.reverse();
      }

      chequeData.push(rowData);
    });

    // Generate table
    doc.autoTable({
      ...TABLE_STYLES,
      columnStyles: {
        0: { cellWidth: 18 }, // Date
        1: { cellWidth: 16 }, // Numéro
        2: { cellWidth: "auto" }, // Type
        3: { cellWidth: 25 }, // Banque
        4: { cellWidth: 25 }, // Titulaire
        5: { cellWidth: 18 }, // Statut Banque
        6: { cellWidth: 18 }, // Facture
        7: { cellWidth: 18 }, // Date échéance
        8: { cellWidth: 20, halign: "right" }, // Montant
      },
      head: [
        reverseIfArabic([
          t("tresorerie.date"),
          t("tresorerie.num"),
          t("tresorerie.type"),
          t("tresorerie.banque"),
          t("tresorerie.titulaire"),
          t("tresorerie.statut"),
          t("tresorerie.factureLabel"),
          t("tresorerie.echeance"),
          t("tresorerie.montant"),
        ]),
      ],
      body: chequeData,
      foot: [
        reverseIfArabic([
          {
            colSpan: 8,
            content: t("tresorerie.total"),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [44, 62, 80],
              textColor: [255, 255, 255],
            },
          },
          {
            content: `${parseFloat(total).toFixed(2)} TND`,
            styles: {
              halign: "right",
              fontStyle: "bold",
              fillColor: [44, 62, 80],
              textColor: [255, 255, 255],
            },
          },
        ]),
      ],
      startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 25 : 40,
    });

    // Prepare summary data
    const statusCounts = data.reduce((acc, item) => {
      acc[item.bankStatus] = (acc[item.bankStatus] || 0) + 1;
      return acc;
    }, {});

    const summaryData = {
      title: t("tresorerie.summary"),
      items: [
        `${t("tresorerie.nombreTotalElements")}: ${data.length}`,
        `${t("tresorerie.montantTotal")}: ${parseFloat(total).toFixed(2)} TND`,
      ],
      breakdowns: [
        {
          title: t("tresorerie.repartitionParType"),
          items: Object.entries(typeCounts).map(([type, count]) => {
            const percentage = ((count / data.length) * 100).toFixed(1);
            const typeLabel =
              type === "CHEQUE"
                ? t("tresorerie.cheques")
                : type === "TRAITE"
                ? t("tresorerie.traites")
                : t("tresorerie.virements");
            return `${typeLabel}: ${count} (${percentage}%)`;
          }),
        },
        {
          title: t("tresorerie.repartitionParStatut"),
          items: Object.entries(statusCounts).map(([status, count]) => {
            const statusText = getChequeStatusLabel(status, t);
            const percentage = ((count / data.length) * 100).toFixed(1);
            return `${statusText}: ${count} (${percentage}%)`;
          }),
        },
      ],
    };

    // Add summary section with page break logic
    const finalY = doc.autoTableEndPosY() + 15;
    const requiredSpace = 80;

    if (needsNewPage(doc, finalY, requiredSpace)) {
      doc.addPage();
      addSummarySection(doc, summaryData, 20, true, locale);
    } else {
      addSummarySection(doc, summaryData, finalY, false, locale);
    }

    addPageNumbers(doc, t);

    const timestamp = moment().format("YYYY-MM-DD_HH-mm");
    const filename = `${t("tresorerie.chequeTraitePdf")}_${timestamp}.pdf`;
    doc.save(filename);

    return {
      success: true,
      message: t("tresorerie.pdfGeneratedSuccessfully"),
      filename,
    };
  } catch (error) {
    console.error("Error generating PDF:", error);
    return {
      success: false,
      message: t("tresorerie.pdfGenerationError"),
      error: error.message,
    };
  }
};

const bordereauPdfHeader = (
  schoolDetails,
  title,
  subTitle = null,
  locale = "fr-FR"
) => {
  const doc = new JsPDF();
  doc.setFont("Amiri");

  // Get logo from settings
  let logoImage = null;
  if (schoolDetails && schoolDetails.logo) {
    logoImage = new Image();
    logoImage.src = schoolDetails.logo;
  }

  // Get current date
  const date = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const formattedDate = date.toLocaleDateString(locale, options);

  // Page dimensions
  const pageWidth = doc.internal.pageSize.getWidth();

  // Add logo on the left (if available)
  if (logoImage && logoImage.src) {
    try {
      doc.addImage(logoImage, "JPEG", 15, 8, 20, 20);
    } catch (error) {
      console.log("Error adding logo:", error);
    }
  }

  // Add building information on the left (next to/below logo)
  doc.setFontSize(10);
  doc.setFont("Amiri", "Bold");
  let leftTextY = 15;

  if (schoolDetails && schoolDetails.name) {
    doc.text(schoolDetails.name, 38, leftTextY);
    leftTextY += 6;
  }

  doc.setFont("Amiri", "normal");
  doc.setFontSize(8);

  if (schoolDetails && schoolDetails.RIB) {
    doc.text(`RIB: ${schoolDetails.RIB}`, 38, leftTextY);
  }

  // Add date on the right
  doc.setFontSize(8);
  doc.text(formattedDate, pageWidth - 15, 15, { align: "right" });

  // Add centered title
  doc.setFontSize(15);
  doc.setFont("Amiri", "Bold");
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pageWidth - titleWidth) / 2;
  doc.text(title, titleX, 35);

  // Add centered subtitle if provided
  if (subTitle) {
    doc.setFontSize(10);
    doc.setFont("Amiri", "normal");
    const subTitleWidth = doc.getTextWidth(subTitle);
    const subTitleX = (pageWidth - subTitleWidth) / 2;
    doc.text(subTitle, subTitleX, 42);
  }

  return doc;
};

/**
 * Generate PDF for bordereaux
 * @param {Object} options - PDF generation options
 * @param {Array} options.data - Filtered bordereau data
 * @param {Object} options.settings - Application settings
 * @param {Object} options.filters - Applied filters (status, statutDepot, search)
 * @returns {Object} - Result object with success status and message
 */
export const generateBordereauPdf = ({
  data,
  settings,
  user,
  filters = {},
  t,
  locale = "fr-FR",
}) => {
  try {
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);
    const schoolDetails = user.building.filter(
      (el) => el.dbName == settings.activeBuilding
    )[0];
    console.log(schoolDetails);
    const filterText = buildFilterText(filters);
    const doc = bordereauPdfHeader(
      schoolDetails,
      t("tresorerie.bordereaux"),
      filterText,
      locale
    );

    // Prepare data and calculate totals
    const bordereauData = [];
    let totalAmount = 0;
    let totalCheques = 0;

    data.forEach((bordereau) => {
      totalAmount += Number(bordereau.montantTotal) || 0;
      totalCheques += Number(bordereau.nombreCheques) || 0;

      let rowData = [
        bordereau.numero || "--",
        moment(bordereau.createdAt).format("DD/MM/YYYY"),
        getBordereauStatusLabel(bordereau.status, t) || "--",
        getBordereauDepotStatusLabel(bordereau.statutDepot, t) || "--",
        bordereau.caisseBank ? bordereau.caisseBank.name : "--",
        bordereau.nombreCheques || "0",
        Number(bordereau.montantTotal).toFixed(2) || "0.00",
      ];

      // Reverse for Arabic if needed
      if (isArabic) {
        rowData.reverse();
      }

      bordereauData.push(rowData);
    });

    // Generate table
    doc.autoTable({
      ...TABLE_STYLES,
      head: [
        reverseIfArabic([
          t("tresorerie.numero"),
          t("tresorerie.dateCreation"),
          t("tresorerie.statut"),
          t("tresorerie.statutDepot"),
          t("tresorerie.caisse"),
          t("tresorerie.nombreCheques"),
          t("tresorerie.montantTotal"),
        ]),
      ],
      body: bordereauData,
      foot: [
        reverseIfArabic([
          {
            colSpan: 6,
            content: t("tresorerie.total").toUpperCase(),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [44, 62, 80],
              textColor: [255, 255, 255],
            },
          },
          {
            content: `${parseFloat(totalAmount).toFixed(2)} TND`,
            styles: {
              halign: "right",
              fontStyle: "bold",
              fillColor: [44, 62, 80],
              textColor: [255, 255, 255],
            },
          },
        ]),
      ],
      startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 25 : 48,
    });

    // Prepare summary data
    const statusCounts = data.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});

    const summaryData = {
      title: t("tresorerie.summary"),
      items: [
        `${t("tresorerie.nombreBordereaux")}: ${data.length}`,
        `${t("tresorerie.nombreTotalCheques")}: ${totalCheques}`,
        `${t("tresorerie.montantTotal")}: ${parseFloat(totalAmount).toFixed(
          2
        )} TND`,
      ],
      breakdowns: [
        {
          title: t("tresorerie.repartitionParStatut"),
          items: Object.entries(statusCounts).map(([status, count]) => {
            const statusText = getBordereauStatusLabel(status, t);
            const percentage = ((count / data.length) * 100).toFixed(1);
            return `${statusText}: ${count} (${percentage}%)`;
          }),
        },
      ],
    };

    // Add summary section with page break logic
    const finalY = doc.autoTableEndPosY() + 15;
    const requiredSpace = 60;

    if (needsNewPage(doc, finalY, requiredSpace)) {
      doc.addPage();
      addSummarySection(doc, summaryData, 20, true, locale);
    } else {
      addSummarySection(doc, summaryData, finalY, false, locale);
    }

    addPageNumbers(doc, t);

    const timestamp = moment().format("YYYY-MM-DD_HH-mm");
    const filename = `bordereaux_${timestamp}.pdf`;
    doc.save(filename);

    return {
      success: true,
      message: t("tresorerie.pdfGeneratedSuccessfully"),
      filename,
    };
  } catch (error) {
    console.error("Error generating bordereau PDF:", error);
    return {
      success: false,
      message: t("tresorerie.pdfGenerationError"),
      error: error.message,
    };
  }
};

/**
 * Generate PDF for a single bordereau with its cheques
 * @param {Object} bordereau - Bordereau data with cheques
 * @param {Object} settings - Application settings
 * @returns {Object} - Result object with success status and message
 */
export const generateSingleBordereauPdf = (
  bordereau,
  settings,
  user,
  t,
  locale = "fr-FR"
) => {
  try {
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);
    const schoolDetails = user.building.filter(
      (el) => el.dbName == settings.activeBuilding
    )[0];
    const doc = bordereauPdfHeader(
      schoolDetails,
      `${t("tresorerie.bordereauPdf")} ${bordereau.numero}`,
      null,
      locale
    );

    // Bordereau details
    const details = [
      [t("tresorerie.numero"), bordereau.numero || "--"],
      [
        t("tresorerie.dateCreation"),
        moment(bordereau.createdAt).format("DD/MM/YYYY"),
      ],
      [
        t("tresorerie.statut"),
        getBordereauStatusLabel(bordereau.status, t) || "--",
      ],
      [
        t("tresorerie.statutDepot"),
        getBordereauDepotStatusLabel(bordereau.statutDepot, t) || "--",
      ],
      [
        t("tresorerie.caisse"),
        bordereau.caisseBank ? bordereau.caisseBank.name : "--",
      ],
      [t("caisse.nombreCheques"), bordereau.nombreCheques || "0"],
      [
        t("tresorerie.montantTotal"),
        `${Number(bordereau.montantTotal).toFixed(2)} TND`,
      ],
    ];

    if (bordereau.description) {
      details.push([t("tresorerie.description"), bordereau.description]);
    }

    // Add bordereau details table
    doc.autoTable({
      theme: "grid",
      styles: { font: "Amiri", valign: "middle", fontSize: 10, cellPadding: 3 },
      headStyles: {
        fontStyle: "bold",
        fontSize: 11,
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold", fillColor: [240, 240, 240] },
        1: { cellWidth: 100 },
      },
      body: details,
      startY: 45,
      margin: { left: 14, right: 14 },
    });

    // Add cheques list if available
    if (bordereau.chequesDetails && bordereau.chequesDetails.length > 0) {
      // Determine columns based on bordereau type
      let tableHeaders, chequesData;

      if (bordereau.type === "CHEQUE") {
        // For CHEQUE type: show dateEchance
        tableHeaders = reverseIfArabic([
          t("tresorerie.numero"),
          t("tresorerie.banque"),
          t("tresorerie.titulaire"),
          t("tresorerie.dateEcheance"),
          t("tresorerie.montant"),
          t("tresorerie.statut"),
        ]);
        chequesData = bordereau.chequesDetails.map((cheque) => {
          let rowData = [
            cheque.num || "--",
            cheque.bank ? cheque.bank.split("-")[0] : "--",
            cheque.titulaire || "--",
            cheque.dateEchance
              ? moment(cheque.dateEchance).format("DD/MM/YYYY")
              : "--",
            Number(cheque.montant).toFixed(2) || "0.00",
            getChequeStatusLabel(cheque.bankStatus, t) || "--",
          ];

          // Reverse for Arabic if needed
          if (isArabic) {
            rowData.reverse();
          }

          return rowData;
        });
      } else {
        // For TRAITE type: show RIB instead of dateEchance
        tableHeaders = reverseIfArabic([
          t("tresorerie.numero"),
          t("tresorerie.banque"),
          "RIB",
          t("tresorerie.montant"),
          t("tresorerie.statut"),
        ]);
        chequesData = bordereau.chequesDetails.map((cheque) => {
          let rowData = [
            cheque.num || "--",
            cheque.bank ? cheque.bank.split("-")[0] : "--",
            cheque.rib || "--",
            Number(cheque.montant).toFixed(2) || "0.00",
            getChequeStatusLabel(cheque.bankStatus, t) || "--",
          ];

          // Reverse for Arabic if needed
          if (isArabic) {
            rowData.reverse();
          }

          return rowData;
        });
      }

      const startY = doc.autoTableEndPosY() + 15;

      // Add section header
      doc.setFontSize(12);
      doc.setFont("Amiri", "Bold");
      const sectionTitle =
        bordereau.type === "CHEQUE"
          ? t("tresorerie.listeDes") + " " + t("tresorerie.cheques")
          : t("tresorerie.listeDes") + " " + t("tresorerie.traites");
      doc.text(sectionTitle, 14, startY - 8);

      doc.setFontSize(9);
      doc.setFont("Amiri", "normal");
      const itemLabel =
        bordereau.type === "CHEQUE"
          ? t("tresorerie.chequesPluriel")
          : t("tresorerie.traitesPluriel");
      doc.text(
        `${t("tresorerie.total")}: ${
          bordereau.chequesDetails.length
        } ${itemLabel}`,
        14,
        startY - 3
      );

      // Generate cheques/traites table
      doc.autoTable({
        theme: "grid",
        styles: {
          font: "Amiri",
          valign: "middle",
          halign: isArabic ? "right" : "center",
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fontStyle: "bold",
          fontSize: 10,
          fillColor: [44, 62, 80],
          textColor: [255, 255, 255],
        },
        head: [tableHeaders],
        body: chequesData,
        startY: startY,
        margin: { left: 14, right: 14 },
        tableWidth: "auto",
        alternateRowStyles: { fillColor: [248, 249, 250] },
      });

      // Add totals section
      const totalAmount = bordereau.chequesDetails.reduce(
        (sum, cheque) => sum + (Number(cheque.montant) || 0),
        0
      );
      const validatedCount = bordereau.chequesDetails.filter(
        (cheque) => cheque.bankStatus === "VALIDATED"
      ).length;
      const pendingCount = bordereau.chequesDetails.filter(
        (cheque) => cheque.bankStatus === "PENDING"
      ).length;

      const finalY = doc.autoTableEndPosY() + 10;
      doc.setFontSize(9);
      doc.setFont("Amiri", "Bold");
      const summaryTitle =
        bordereau.type === "CHEQUE"
          ? t("tresorerie.summary") + " " + t("tresorerie.cheques") + ":"
          : t("tresorerie.summary") + " " + t("tresorerie.traites") + ":";
      doc.text(summaryTitle, 14, finalY);

      doc.setFont("Amiri", "normal");
      doc.text(
        `${t("tresorerie.montantTotal")}: ${totalAmount.toFixed(2)} TND`,
        14,
        finalY + 4
      );
      const validatedLabel =
        bordereau.type === "CHEQUE"
          ? t("tresorerie.valides")
          : t("tresorerie.valides");
      const pendingLabel = t("tresorerie.enAttente");
      const itemLabelSingular =
        bordereau.type === "CHEQUE"
          ? t("tresorerie.chequesPluriel")
          : t("tresorerie.traitesPluriel");
      doc.text(
        `${validatedLabel}: ${validatedCount} ${itemLabelSingular}`,
        14,
        finalY + 7
      );
      doc.text(
        `${pendingLabel}: ${pendingCount} ${itemLabelSingular}`,
        14,
        finalY + 10
      );
    } else {
      const startY = doc.autoTableEndPosY() + 15;
      doc.setFontSize(10);
      doc.setFont("Amiri", "normal");
      doc.text(t("tresorerie.aucunCheque"), 14, startY);
    }

    const filename = `bordereau_${bordereau.numero}_${moment().format(
      "YYYY-MM-DD"
    )}.pdf`;
    doc.save(filename);

    return {
      success: true,
      message: t("tresorerie.pdfGeneratedSuccessfully"),
      filename,
    };
  } catch (error) {
    console.error("Error generating single bordereau PDF:", error);
    return {
      success: false,
      message: t("tresorerie.pdfGenerationError"),
      error: error.message,
    };
  }
};
